import { alpha } from "@mui/material/styles";
import createTheme from "@mui/material/styles/createTheme";
import defaultShadows, { Shadows } from "@mui/material/styles/shadows";
import { gridClasses } from "@mui/material/Grid";

import { Colors, colors } from "./colors";
import { palette } from "./palette";
import { typography } from "./typography";

declare module "@mui/material/styles/createTheme" {
  interface Theme {
    colors: Colors;
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    colors?: Colors;
  }
}

export const INPUT_HEIGHT = 8;
export const SELECT_MENU_Y_MARGIN = 1;
export const SLIDER_HEIGHT = 0.5;
export const SLIDER_THUMB_SIZE = 4;

const placeholder = {
  color: palette.text.secondary,
  opacity: 1,
};

export const spacing = 8;
const shadows = [
  "none",
  `0px ${spacing * 0.5}px ${spacing * 1}px ${alpha(
    palette.common.black,
    0.08
  )}`,
  `0px ${spacing * 1}px ${spacing * 2}px ${alpha(palette.common.black, 0.08)}`,
].concat(defaultShadows) as Shadows;

export const theme = createTheme({
  colors,
  spacing,
  palette,
  typography,
  shadows,
  shape: {
    borderRadius: spacing * 1.5,
  },
  components: {
    MuiButton: {
      defaultProps: {
        variant: "contained",
        disableElevation: true,
        color: "primary",
      },
      styleOverrides: {
        sizeSmall: {
          height: spacing * 5,
          padding: `${spacing * 0}px ${spacing * 2}px`,
          fontSize: "1.125rem",
        },
        sizeLarge: {
          fontSize: "1.25rem",
        },
        containedSizeSmall: {
          fontSize: "1.125rem",
          lineHeight: 1.25,
        },
        root: {
          fontWeight: 400,
          height: spacing * 8,
          padding: `${spacing * 0}px ${spacing * 4}px`,
          textTransform: "none",
          borderRadius: spacing * 8,
        },
        outlined: {
          height: spacing * 8,
          padding: `0px ${spacing * 4}px`,
        },
        outlinedPrimary: {
          "&$focusVisible": {
            backgroundColor: colors.green4,
          },
          "&:hover": {
            backgroundColor: colors.green2,
            color: palette.primary.contrastText,
            "@media (hover: none)": {
              backgroundColor: colors.green2,
            },
          },
          "&$disabled": {
            opacity: 0.3,
            color: colors.green2,
            border: `1px solid ${colors.green2}`,
          },
        },
        containedPrimary: ({ ownerState }) => ({
          "&$focusVisible": {
            backgroundColor: colors.green4,
          },
          "&:hover": {
            backgroundColor: colors.green1,
          },
          ...(ownerState.disabled && {
            opacity: 0.3,
            backgroundColor: `${colors.green2} !important`,
            color: `${palette.primary.contrastText} !important`,
          }),
        }),
      },
    },
    MuiButtonBase: {
      // The properties to apply
      defaultProps: {
        disableRipple: true, // No more ripple, on the whole application 💣!
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          fontSize: "1.25rem",
          lineHeight: "1.75rem",
        },
      },
    },
    MuiCard: {
      defaultProps: {
        variant: "outlined",
      },
      styleOverrides: {
        root: {
          borderRadius: spacing * 2,
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        subheaderTypographyProps: {
          variant: "body2",
        },
      },
      styleOverrides: {
        root: {
          padding: 0,
        },
        subheader: {
          marginTop: spacing * 0.5,
          color: palette.text.disabled,
        },
        action: {
          marginTop: 0,
          marginRight: 0,
        },
      },
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: false,
      },
      styleOverrides: {
        root: {
          padding: `${spacing * 0}px ${spacing * 3}px`,
          maxWidth: spacing * 159,
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        color: "inherit",
      },
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        elevation: 1,
        anchorEl: null,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
      },
      styleOverrides: {
        paper: {
          border: "0.5px solid var(--lightGray)",
          margin: `${spacing * SELECT_MENU_Y_MARGIN}px 0`,
        },
        list: {
          padding: spacing * 1,
        },
      },
    },
    MuiSlider: {
      defaultProps: {
        color: "secondary",
      },
      styleOverrides: {
        root: {
          height: spacing * SLIDER_HEIGHT,
          padding: `${(spacing * SLIDER_THUMB_SIZE) / 2}px ${spacing * 0}px`,
        },
        rail: {
          height: spacing * SLIDER_HEIGHT,
          borderRadius: spacing * (SLIDER_HEIGHT / 2),
          color: palette.divider,
          opacity: undefined,
          margin: `0 ${spacing * -3}px`,
          width: `calc(100% + ${spacing * 6}px)`,
        },
        marked: {
          marginBottom: spacing * 1.5,
        },
        mark: {
          display: "block",
          backgroundColor: "white",
          padding: 2,
          borderRadius: "50%",
          transform: "translate(-50%, -50%)",
          border: `1px solid ${palette.secondary.main}`,
        },
        markLabel: {
          ...typography.subtitle1,
          '&[data-index="0"]': {
            transform: `translateX(${spacing * -3}px)`,
          },
          top: spacing * 5,
        },
        markLabelActive: {
          color: undefined,
        },
        track: {
          height: spacing * SLIDER_HEIGHT,
          borderRadius: spacing * (SLIDER_HEIGHT / 2),
          marginLeft: spacing * -3,
          paddingRight: spacing * 3,
        },
        thumb: {
          width: spacing * SLIDER_THUMB_SIZE,
          height: spacing * SLIDER_THUMB_SIZE,
          backgroundColor: palette.common.white,
          borderWidth: 1.5,
          borderStyle: "solid",
          borderColor: "currentColor",
          boxShadow: shadows[1],
          "&$focusVisible,&:hover": {
            boxShadow: undefined,
          },
          "&$active": {
            boxShadow: undefined,
          },
        },
        thumbColorSecondary: {
          "&$focusVisible,&:hover": {
            boxShadow: undefined,
          },
          "&$active": {
            boxShadow: undefined,
          },
        },
        valueLabel: {
          left: "calc(50% - 16px)",
        },
      },
    },
    MuiSwitch: {
      defaultProps: {
        color: "primary",
      },
      styleOverrides: {
        root: {
          width: spacing * 7,
          height: spacing * 4,
          padding: 0,
        },
        switchBase: {
          padding: spacing * 0.5,
          color: colors.brown,
          [`&.Mui-checked`]: {
            color: "white",
            transform: "translateX(24px)",
          },
          [`&.Mui-checked.Mui-disabled`]: {
            color: "white",
            transform: "translateX(24px)",
          },
          [`&.Mui-checked+.MuiSwitch-track`]: {
            opacity: 1,
          },
          [`&.Mui-checked.Mui-disabled+.MuiSwitch-track`]: {
            opacity: 0.5,
          },
        },
        colorPrimary: {
          "&$checked": {
            color: palette.primary.contrastText,
          },
        },
        thumb: {
          marginTop: spacing * 0.05,
          width: spacing * 3,
          height: spacing * 3,
          boxShadow: undefined,
        },
        track: {
          backgroundColor: palette.common.white,
          borderWidth: 2,
          borderRadius: spacing * 2,
          borderStyle: "solid",
          borderColor: palette.divider,
          transition: [
            "background-color 200ms linear",
            "border-color 200ms linear",
          ],
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        disableFocusListener: true,
        disableHoverListener: true,
        disableTouchListener: true,
      },
      styleOverrides: {
        tooltip: {
          border: "0.5px solid var(--lightGray)",
          backgroundColor: palette.common.white,
          color: palette.text.disabled,
          boxShadow: shadows[2],
          padding: spacing * 3,
          borderRadius: spacing * 2,
          maxWidth: 320,
          margin: `${spacing}px 0`,
          ...typography.body2,
        },
      },
    },
    MuiPopper: {
      defaultProps: {
        placement: "bottom-start",
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          border: `1px solid ${palette.grey[300]}`,
          borderRadius: spacing * 2,
          boxShadow: "none",
        },
        rounded: {
          "&:last-child": {
            borderBottomLeftRadius: spacing * 2,
            borderBottomRightRadius: spacing * 2,
          },
          "&:first-child": {
            borderTopLeftRadius: spacing * 2,
            borderTopRightRadius: spacing * 2,
          },
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          "&:hover $focusHighlight": {
            opacity: 0,
          },
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: spacing * 3,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 0,
        },
        disabled: {
          color: palette.text.disabled,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorSecondary: {
          backgroundColor: palette.secondary.light,
          color: palette.common.white,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: "1.125rem",
          backgroundColor: palette.common.white,
          "--lightGray": "#ECF1F0",
          " -ms-overflow-style": "none" /* for Internet Explorer, Edge */,
          scrollbarWidth: "none",
          overflowY: "scroll",
        },
        "body::-webkit-scrollbar": {
          display: "none" /* for Chrome, Safari, and Opera */,
        },
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          display: "flex",
          marginLeft: undefined,
          marginRight: undefined,
        },
        label: {
          marginLeft: spacing * 2,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          ...typography.subtitle1,
        },
        contained: {
          marginTop: spacing * 1,
          marginLeft: 0,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          ...typography.body2,
          color: palette.text.disabled,
          marginBottom: spacing * 1,
          "&$focused": {
            color: undefined,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          height: spacing * INPUT_HEIGHT,
          "&$disabled": {
            ...typography.body2,
            color: colors.grey2,
          },
        },
        input: {
          "&::-webkit-input-placeholder": placeholder,
          "&::-moz-placeholder": placeholder,
          "&:-ms-input-placeholder": placeholder,
          "&::-ms-input-placeholder": placeholder,
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        "spacing-xs-3": {
          [`& > .${gridClasses.item}`]: {
            padding: `${spacing * 1.5}px !important`,
          },
          margin: `${spacing * -1.5}px !important`,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        formControl: {
          position: undefined,
          transform: undefined,
        },
        shrink: {
          transform: undefined,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingTop: spacing * 1,
          paddingBottom: spacing * 1,
          borderRadius: spacing * 1,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: spacing * INPUT_HEIGHT,
          "&.Mui-disabled": {
            backgroundColor: palette.action.disabledBackground,
          },
          "&:hover $notchedOutline": {
            borderColor: palette.text.disabled,
          },
          // Reset on touch devices, it doesn't add specificity
          "@media (hover: none)": {
            "&:hover $notchedOutline": {
              borderColor: palette.divider,
            },
          },
          "&$focused $notchedOutline": {
            borderColor: palette.primary.light,
            borderWidth: 2,
          },
          "&$disabled $notchedOutline": {
            borderWidth: 0,
          },
        },
        multiline: {
          height: spacing * INPUT_HEIGHT * 2,
        },
        input: {
          height: "100%",
          display: "flex",
          alignItems: "center",
          padding: `${spacing * 0}px ${spacing * 2}px`,
        },
        notchedOutline: {
          borderColor: palette.divider,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          borderColor: palette.action.disabled,
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          "&:focus": {
            backgroundColor: undefined,
          },
          height: undefined,
        },
        outlined: {
          "&&": {
            paddingRight: spacing * 7,
          },
        },
        icon: {
          color: palette.text.secondary,
        },
        iconOutlined: {
          right: spacing * 2,
        },
        iconOpen: {
          color: palette.text.disabled,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          bottomBorder: 0,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: 0,
          boxShadow: "0px 1px 0px #EEEEEE",
          paddingBottom: spacing * 3,
          paddingLeft: spacing * 1.5,
          paddingRight: spacing * 1.5,
        },
        head: {
          paddingTop: spacing * 2,
        },
        body: {
          paddingTop: spacing * 3,
        },
      },
    },

    MuiTableBody: {
      styleOverrides: {
        root: {
          "& > :last-child > td": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        iconContainer: {
          "&$alternativeLabel": {
            marginRight: spacing * 3,
          },
        },
        root: {
          display: "flex",
          "&$alternativeLabel": {
            flexDirection: "row",
          },
          "&.Mui-active": {
            "&$label": {
              color: palette.primary.main,
            },
          },
        },
        label: {
          "&$alternativeLabel": {
            ...typography.body1,
            lineHeight: 2,
            marginTop: 0,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: spacing * 3,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          justifyContent: "flex-start",
          padding: 0,
        },
        spacing: {
          padding: spacing,
          "& > :not(:first-child)": {
            margin: spacing,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
  },
});

export type CoverTreeTheme = typeof theme;

export { colors };
